/**
 * Initialises and manages the behaviour of the main site navigation,
 * specifically handling mega menu dropdowns for both touch and mouse devices.
 *
 * @param {Document|HTMLElement} [root=document] - The root element to search within for the navigation.
 */
function navigation(root = document) {
	// Find the main navigation container.
	const nav = root.querySelector('.site-header__navigation');
	// If the navigation doesn't exist, stop the script.
	if (!nav) {
		return;
	}

	// --- Device & Element Detection ---

	// Detect if the current device is touch-enabled to apply the correct event listeners.
	const isTouch =
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0;

	// Define selectors for key elements to make the code easier to read and maintain.
	const itemSel = '.site-header__navigation-list-item';
	const linkSel = '.site-header__navigation-list-link';
	const panelSel = '.mega';

	// Find all top-level navigation items that contain a mega menu.
	const parents = Array.from(
		nav.querySelectorAll(`${ itemSel }.site-header__navigation-list-item--has-mega`)
	);

	// --- Helper Functions ---

	// A quick way to get the main link or mega menu panel from a list item.
	const getLink = (li) => li.querySelector(linkSel);
	const getPanel = (li) => li.querySelector(panelSel);

	/**
	 * Opens a specific mega menu panel.
	 * It adds the necessary active classes and updates ARIA attributes for accessibility.
	 *
	 * @param {HTMLElement} li The parent list item of the menu to open.
	 */
	const openMenu = (li) => {
		const panel = getPanel(li);
		if (!panel) {
			return;
		}

		li.classList.add('site-header__navigation-list-item--active');
		panel.classList.add('mega--active');
		panel.removeAttribute('inert');
		li.setAttribute('aria-expanded', 'true');
	};

	/**
	 * Closes a specific mega menu panel.
	 * It removes active classes and resets ARIA attributes.
	 *
	 * @param {HTMLElement} li The parent list item of the menu to close.
	 */
	const closeMenu = (li) => {
		const panel = getPanel(li);
		if (!panel) {
			return;
		}

		li.classList.remove('site-header__navigation-list-item--active');
		panel.classList.remove('mega--active');
		panel.setAttribute('inert', '');
		li.setAttribute('aria-expanded', 'false');
	};

	/**
	 * A convenience function to close all open mega menus.
	 */
	const closeAllMenus = () => parents.forEach(closeMenu);

	// --- Initialisation ---

	// Set initial ARIA attributes on all mega menu parents for accessibility.
	parents.forEach((li) => {
		const panel = getPanel(li);
		if (!panel) {
			return;
		}
		panel.setAttribute('inert', '');
		li.setAttribute('aria-haspopup', 'true');
		li.setAttribute('aria-expanded', 'false');
	});

	// --- Global Event Listeners ---

	// Handles clicks outside the navigation to close any open menus.
	document.addEventListener('click', (e) => {
		const backButton = e.target.closest('.mega__back-button');
		if (backButton) {
			const parentMenuItem = backButton.closest(itemSel);
			if (parentMenuItem) {
				closeMenu(parentMenuItem);
			}
			return;
		}

		if (!nav.contains(e.target)) {
			closeAllMenus();
		}
	});

	// Handles closing an open menu when the 'Escape' key is pressed.
	document.addEventListener('keydown', (e) => {
		if (e.key !== 'Escape') {
			return;
		}

		const openItem = nav.querySelector(`${ itemSel }[aria-expanded="true"]`);
		if (openItem) {
			closeMenu(openItem);
			getLink(openItem)?.focus();
		}
	});

	// --- Item-Specific Event Listeners ---

	let hoverTimer;
	const HOVER_DELAY = 120; // in milliseconds

	parents.forEach((li) => {
		const link = getLink(li);
		const panel = getPanel(li);
		if (!link || !panel) {
			return;
		}

		// --- Desktop (Mouse) Behaviour ---
		if (!isTouch) {
			li.addEventListener('mouseenter', () => {
				clearTimeout(hoverTimer);
				hoverTimer = setTimeout(() => {
					closeAllMenus();
					openMenu(li);
				}, HOVER_DELAY);
			});
			li.addEventListener('mouseleave', () => {
				clearTimeout(hoverTimer);
				hoverTimer = setTimeout(() => closeMenu(li), HOVER_DELAY);
			});

			li.addEventListener('keydown', (e) => {
				const items = Array.from(panel.querySelectorAll(linkSel));
				const current = document.activeElement;
				const idx = items.indexOf(current);

				switch (e.key) {
					case 'Enter':
					case ' ':
						if (panel.hasAttribute('inert')) {
							e.preventDefault();
							closeAllMenus(); openMenu(li);
							items[ 0 ]?.focus();
						}
						break;
					case 'ArrowDown':
						if (items.length) {
							e.preventDefault();
							items[ (idx + 1 + items.length) % items.length ].focus();
						}
						break;
					case 'ArrowUp':
						if (items.length) {
							e.preventDefault();
							items[ (idx - 1 + items.length) % items.length ].focus();
						}
						break;
					case 'ArrowRight': {
						e.preventDefault();
						const topItems = Array.from(nav.querySelectorAll(`> ul > ${ itemSel }`));
						const i = topItems.indexOf(li);
						const next = topItems[ (i + 1) % topItems.length ];
						closeAllMenus(); openMenu(next); getLink(next)?.focus();
						break;
					}
					case 'ArrowLeft': {
						e.preventDefault();
						const topItems = Array.from(nav.querySelectorAll(`> ul > ${ itemSel }`));
						const i = topItems.indexOf(li);
						const prev = topItems[ (i - 1 + topItems.length) % topItems.length ];
						closeAllMenus(); openMenu(prev); getLink(prev)?.focus();
						break;
					}
					case 'Escape':
						e.preventDefault();
						closeMenu(li);
						link.focus();
						break;
				}
			});
		}

		// --- Touch & Click Behaviour ---
		link.addEventListener('click', (e) => {
			if (!panel) {
				return;
			}

			e.stopImmediatePropagation();

			// This is the parent of the whole nav, which gets an 'active' class when the mobile menu is open.
			const mobileNavIsActive = nav.parentElement.classList.contains('site-navigation--active');
			const isExpanded = li.getAttribute('aria-expanded') === 'true';

			// On mobile, the first click should ALWAYS open the submenu.
			// On desktop, this condition won't be met, so the logic proceeds as before.
			if (mobileNavIsActive && !isExpanded) {
				e.preventDefault();
				closeAllMenus(); // Close any other open menus
				openMenu(li);
				return; // Stop here for the mobile case.
			}

			// The original logic for desktop clicks and second taps on mobile.
			const isHashLink = link.getAttribute('href').trim() === '#';
			if (isHashLink) {
				e.preventDefault();
			}

			if (!isExpanded) {
				e.preventDefault();
				closeAllMenus();
				openMenu(li);
			} else if (isHashLink) {
				closeAllMenus();
			}
		});
	});
}

/**
 * Handles the click event for the mobile navigation icon (hamburger menu).
 */
function navicon() {
	document.querySelector('.site-navigation__navicon')?.addEventListener('click', (e) => {
		e.preventDefault();
		e.currentTarget.classList.toggle('site-navigation__navicon--active');
		document.querySelector('.site-navigation')?.classList.toggle('site-navigation--active');
		document.body.classList.toggle('site-navigation--open');
	});
}

export { navigation, navicon };
